import { useEffect, useState } from 'react';
import { toDataURL } from 'qrcode';
import { BotonSecundario, Firma, Pantalla, Pasos, Seguro } from './ui.js';

/**
 * El puente: de la pantalla grande al teléfono.
 *
 * ── POR QUÉ EXISTE ─────────────────────────────────────────────────────────────
 *
 * El enlace llega por correo o por la web del cliente, y mucha gente lo abre donde lo leyó:
 * en el escritorio. Ahí este flujo es cuesta arriba — la webcam es peor que la cámara del
 * teléfono, girar un DNI frente a un monitor es incómodo, y el NFC ni existe. La verificación
 * está pensada para el celular, y esta pantalla es el camino: el MISMO enlace, en QR, para
 * seguirlo ahí.
 *
 * ── EL SALTO DE DISPOSITIVO ES LEGAL, Y ESTÁ DECIDIDO EN EL CORE ───────────────
 *
 * `sesion.ts` lo dice: **el enlace no es la frontera de seguridad** — el QR obliga a que la
 * sesión salte de dispositivo, así que atarla a un navegador es imposible por diseño. Lo que
 * protege es de quién es la cara. Por eso este QR no pide nada nuevo: es la misma URL.
 *
 * ── SIEMPRE HAY SALIDA ─────────────────────────────────────────────────────────
 *
 * "Continuar en este dispositivo" no es un detalle: la heurística de escritorio puede
 * equivocarse, y hay gente cuya única cámara ES la webcam. Un puente sin salida convierte
 * un consejo en un muro, y el muro deja fuera justo a quien menos alternativas tiene.
 *
 * TODO(estado): cuando exista un endpoint público de estado de sesión, el escritorio podrá
 * detectar que el teléfono terminó y enseñar el resultado aquí. Hoy no existe, y esta
 * pantalla no finge seguirlo.
 */

/**
 * ¿Parece un escritorio? Conservadora a propósito: solo dice que sí sin tocar (cero puntos
 * táctiles) Y con puntero fino. Ante la duda dice que no, y el flujo normal sigue — mejor
 * no enseñar el puente a un tablet raro que ponérselo delante a un teléfono.
 */
export function pareceEscritorio(): boolean {
  return navigator.maxTouchPoints === 0 && window.matchMedia('(pointer: fine)').matches;
}

export function Puente({
  onContinuarAqui,
  onVolver,
}: {
  onContinuarAqui: () => void;
  onVolver?: () => void;
}) {
  const [qr, setQr] = useState<string | null>(null);

  useEffect(() => {
    // El QR es la URL tal cual está en la barra: token incluido. No viaja a ningún sitio —
    // `toDataURL` corre aquí, en el navegador; el enlace no sale de la pantalla.
    toDataURL(window.location.href, { width: 240, margin: 1 })
      .then(setQr)
      .catch(() => {
        // Sin QR no hay puente que valga: se sigue aquí y ya. Es el único "fallo" de esta
        // pantalla y su remedio es no estorbar.
        onContinuarAqui();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Pantalla>
      <Pasos paso={1} onVolver={onVolver} />

      <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
        <Seguro />
        <h1 className="mt-4 text-[26px] font-bold leading-tight">Continúa en tu celular</h1>
        <p className="mt-2 max-w-[300px] text-[14px] leading-relaxed" style={{ color: 'rgba(16,17,20,.6)' }}>
          Escanea el código con la cámara de tu celular. La cámara de tu teléfono captura tu
          documento mucho mejor que una webcam.
        </p>

        <div
          className="mt-7 grid place-items-center rounded-3xl p-5"
          style={{ border: '1.5px solid rgba(51,50,242,.25)', background: '#fff' }}
        >
          {qr !== null ? (
            <img src={qr} alt="Código QR de este enlace de verificación" width={240} height={240} />
          ) : (
            <div style={{ width: 240, height: 240 }} aria-hidden />
          )}
        </div>

        <div className="mt-8 w-full">
          <BotonSecundario onClick={onContinuarAqui}>Continuar en este dispositivo</BotonSecundario>
        </div>
      </div>

      <Firma />
    </Pantalla>
  );
}
